<template>
  <!--
    Our <Dialog> component isn't very ideal for this as we want the preview
    iframe to take as much space as it can, which it can't do, so let's make
    this a special excemption.
  -->
  <Teleport to="#app-scaffold">
    <div class="fixed inset-0 z-30 flex flex-col bg-white">
      <div
        class="flex h-[52px] shrink-0 items-center justify-between gap-2 border-b border-gray-300 px-4"
      >
        <span
          class="text-sm font-bold whitespace-nowrap overflow-hidden text-ellipsis"
        >
          Email preview
        </span>

        <div class="flex items-center gap-4">
          <Menu.Dropdown :label="contextLabel">
            <Menu.SectionHeader>Form</Menu.SectionHeader>
            <Menu.Item
              v-for="context in relevantContexts"
              :key="context"
              :icon="CONTEXT_TO_CONFIGURATION[context].icon"
              :label="CONTEXT_TO_CONFIGURATION[context].title"
              :active="mode === context"
              @click="mode = context"
            />

            <Menu.SectionHeader>Device presets</Menu.SectionHeader>
            <Menu.Item
              v-for="dev in RESPONSIVE_DEVICES"
              :key="dev.key"
              :label="dev.name"
              :icon="dev.icon"
              :active="dev.key === responsiveSize.key"
              @click="
                responsiveSize = {
                  key: dev.key,
                  width: dev.width,
                  height: dev.height,
                }
              "
            />
          </Menu.Dropdown>

          <button
            type="button"
            class="rounded-lg p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-800"
            @click="emit('close')"
          >
            <XMarkIcon class="size-4" />
          </button>
        </div>
      </div>

      <ResizableIframe v-model:responsive-size="responsiveSize">
        <EmailPreview :mode="mode" :body="props.body" :status="props.status" />
      </ResizableIframe>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, shallowRef } from "vue";

import Menu from "@/design_system/Menu";
import XMarkIcon from "@/icons/heroicons/x-mark-micro.svg";
import { useStore as useScaffoldingStore } from "@/store/stores/scaffolding";
import { Context } from "@/types/preview";
import { RenderedMarkdown } from "@/utils";

import ResizableIframe from "./components/ResizableIframe.vue";
import EmailPreview from "./EmailPreview.vue";
import { RESPONSIVE_DEVICES, ResponsiveSize } from "./responsive";
import { CONTEXT_TO_CONFIGURATION } from "./types";

const store = useScaffoldingStore();

const props = withDefaults(
  defineProps<{
    status: "success" | "pending";
    body: RenderedMarkdown;
    contexts?: Context[];
  }>(),
  {
    contexts: () => ["email", "web", "email_free", "web_free"],
  }
);

const emit = defineEmits<{
  close: [];
}>();

const mode = ref<Context>(props.contexts[0]);

const responsiveSize = shallowRef<ResponsiveSize>({ width: 540, height: 720 });
const responsiveDevice = computed(() => responsiveSize.value.key);

const relevantContexts = computed(() => {
  const newsletter = store.newsletter;

  return props.contexts.filter((context) =>
    context === "web"
      ? newsletter?.enabled_features.includes("archives")
      : context === "email_free"
      ? newsletter?.paid_subscriptions_status === "active"
      : true
  );
});

const contextLabel = computed(() => {
  const key = responsiveDevice.value;

  const config = CONTEXT_TO_CONFIGURATION[mode.value];
  const device = key && RESPONSIVE_DEVICES.find((dev) => dev.key === key);

  return config.title + (device ? ` · ` + device.name : "");
});
</script>
